import { CCPermissions } from "./CCPermissions";
import { CCStructure } from "./CCStructure";
import { CCStyling, DefaultStyling } from "./CCStyling";
import { convertAttrsToString, convertInputVariantToType } from "./CCUtility";
import { CCHeader, CCText, CCWrapper } from "./components/Form";
import { CCGrid, CCHyperlink, CCInstructions } from "./components/UI";
import {
  CCStructureBasicInputItem,
  CCStructureBasicInputItemType,
  CCStructureItem,
  CCStructureItemGeneric,
  CCStructureItemType,
  CCStructureNote
} from "./types/structure";
import { CCInputVariant } from "./types/variants";

function parseStyle(item: AbstractCCStructureItem) {
  return CCStyling.join(
    item.isMentorField && item.type === "header"
      ? CCStyling.fromObject({
          "font-weight": "bold",
          color: "darkblue"
        })
      : DefaultStyling.blank,
    item.child
      ? CCStyling.fromObject({
          width: "100%"
        })
      : DefaultStyling.blank,
    item.type === "paragraph"
      ? CCStyling.fromObject({
          display: "block"
        })
      : DefaultStyling.blank,
    item.additionalCss ?? DefaultStyling.blank
  );
}
function convertClassArrayToAttr(array: string[]): string {
  return 'class="' + array.join(" ") + '"';
}
function getInputItemClassList(item: AbstractCCInputItem) {
  return [
    ...(item.type === "paragraph" && item["richText"]
      ? ["richTextEnabled"]
      : []),
    ...(item.yearAgnostic ? ["yearAgnostic"] : []),
    ...(item.optional ? ["cc-optional"] : []),
    "form-control",
    `conf${item.type[0].toUpperCase() + item.type.substring(1)}`
  ];
}
function getGenericInputItemAttrs(
  variant: CCInputVariant,
  styling: CCStyling,
  key: string,
  manualStyle?: CCStyling
) {
  return (
    `type="${convertInputVariantToType(variant)}" ` +
    `data-key="${key}" ` +
    `value="" ` +
    `style="${CCStyling.join(manualStyle ?? DefaultStyling[variant], styling).compile()}"`
  );
}

export abstract class AbstractCCStructureItem {
  public type: CCStructureItemType;
  public additionalCss?: CCStyling;
  public htmlAttributes?: Record<string, string>;
  public notes?: CCStructureNote | CCStructureNote[];
  public child?: boolean;
  public orientation?: "vertical" | "horizontal";
  public isMentorField?: boolean;

  constructor(type: CCStructureItemType, obj: CCStructureItemGeneric) {
    this.type = type;
    this.additionalCss = obj.additionalCss ?? DefaultStyling.blank;
    this.htmlAttributes = obj.htmlAttributes ?? {};
    this.notes = obj.notes;
    this.child = obj.child;
    this.orientation = obj.orientation;
    this.isMentorField = obj.isMentorField;
  }
  public abstract generateHTML(): string;

  public processNotes(root: JQuery) {
    const buffer = [];
    if (this.notes != undefined) {
      if (!Array.isArray(this.notes)) this.notes = [this.notes];
      for (const note of this.notes) {
        if (note.addRef !== false) {
          // Last appended node's first child
          const noteTarget = root.children().last().children().first();
          const text = noteTarget.text().trim();
          if (text.endsWith(":")) {
            noteTarget.text(
              text.substring(0, text.length - 1) + (note.ref || "*") + ": "
            );
          } else {
            noteTarget.text(text + (note.ref || "*"));
          }
        }
        if (note.type === "immediate") {
          root.append(
            CCWrapper(
              `<span>${note.ref + note.text}</span>`,
              "",
              DefaultStyling.note
            )
          );
        } else {
          buffer.push(note);
        }
      }
    }
    return buffer;
  }
  public static getInstance(
    obj: CCStructureItem,
    indexInStructure: number,
    struct: CCStructure
  ) {
    return new _abstract_map[obj.type](obj, indexInStructure, struct);
  }
  public static itemIsInput(
    item: AbstractCCStructureItem
  ): item is AbstractCCInputItem {
    return "key" in item && item.type !== "custom";
  }
  public static itemIsGrid(item: AbstractCCStructureItem): item is CCGridItem {
    return item.type === "grid";
  }
  public static itemIsCustom(
    item: AbstractCCStructureItem
  ): item is CCCustomItem {
    return item.type === "custom";
  }
}

// Base class for basic input items to reduce code duplication
abstract class AbstractCCInputItem extends AbstractCCStructureItem {
  public key: string;
  public optional?: boolean;
  public readOnly?: boolean;
  public yearAgnostic?: boolean;
  public name?: string;
  public text?: string;

  constructor(
    type: CCStructureBasicInputItemType,
    obj: CCStructureBasicInputItem
  ) {
    super(type, obj);
    this.key = obj.key;
    this.optional = obj.optional;
    this.readOnly = obj.readOnly;
    this.yearAgnostic = obj.yearAgnostic;
    this.name = obj.name;
    this.text = obj.text;
  }
  public element() {
    const variant = this.type[0].toUpperCase() + this.type.substring(1);
    return $(`.conf${variant}[data-key="${this.key}"]`);
  }
  public abstract updateWithData(data: string): boolean;

  protected lengthError(data: string) {
    return !this.optional && data?.trim()?.length === 0;
  }
  protected numberError(data: string) {
    return isNaN(Number(data));
  }
  protected setValue(data: string) {
    const element = this.element();
    element.attr("value", data);
    element.val(data);
  }
  private resolvePermissions(permissions: Readonly<CCPermissions>) {
    if (permissions.writableAsMenteeIdentity() && !this.isMentorField) {
      return true;
    }
    if (permissions.writableAsMentorIdentity() && this.isMentorField) {
      return true;
    }
  }
  public refreshPermissions(permissions: Readonly<CCPermissions>) {
    const element = this.element();
    const children = element.children();
    element.prop("readonly", this.readOnly);
    children.prop("readonly", this.readOnly);
    if (this.resolvePermissions(permissions)) {
      element.removeProp("disabled");
      element.removeAttr("disabled");
      children.removeProp("disabled");
      children.removeAttr("disabled");
    } else {
      element.prop("disabled", true);
      children.prop("disabled", true);
    }
  }
}

export class CCStringItem extends AbstractCCInputItem {
  constructor(obj: CCStructureItem<"string">) {
    super("string", obj);
  }
  public generateHTML(): string {
    let content: string = "";
    const styling = parseStyle(this);
    if (this.name) {
      content = CCText("h5", "", this.name + ": ", styling);
    }
    const attrs = convertAttrsToString(this.htmlAttributes);
    const classList = getInputItemClassList(this);
    const classAttr = convertClassArrayToAttr(classList);
    const generic = getGenericInputItemAttrs("String", styling, this.key);
    const html = CCWrapper(
      content + `<input ${classAttr} ${attrs} ${generic} />`
    );
    return this.text
      ? CCWrapper(CCText("h5", "", this.text, DefaultStyling.inputText) + html)
      : html;
  }
  public updateWithData(data: string) {
    super.setValue(data);
    return super.lengthError(data);
  }
}

export class CCIntegerItem extends AbstractCCInputItem {
  constructor(obj: CCStructureItem<"integer">) {
    super("integer", obj);
  }
  public generateHTML(): string {
    let content: string = "";
    const styling = parseStyle(this);
    if (this.name) {
      content = CCText("h5", "", this.name + ": ", styling);
    }
    const attrs = convertAttrsToString(this.htmlAttributes);
    const classList = getInputItemClassList(this);
    const classAttr = convertClassArrayToAttr(classList);
    const generic = getGenericInputItemAttrs("Integer", styling, this.key);
    const html = CCWrapper(
      content + `<input ${classAttr} ${attrs} ${generic} />`
    );
    return this.text
      ? CCWrapper(CCText("h5", "", this.text, DefaultStyling.inputText) + html)
      : html;
  }
  public updateWithData(data: string) {
    super.setValue(data);
    return super.lengthError(data) || super.numberError(data);
  }
}

export class CCFloatItem extends AbstractCCInputItem {
  constructor(obj: CCStructureItem<"float">) {
    super("float", obj);
  }
  public generateHTML(): string {
    let content: string = "";
    const styling = parseStyle(this);
    if (this.name) {
      content = CCText("h5", "", this.name + ": ", styling);
    }
    const attrs = convertAttrsToString(this.htmlAttributes);
    const classList = getInputItemClassList(this);
    const classAttr = convertClassArrayToAttr(classList);
    const generic = getGenericInputItemAttrs("Float", styling, this.key);
    const html = CCWrapper(
      content + `<input ${classAttr} ${attrs} ${generic} />`
    );
    return this.text
      ? CCWrapper(CCText("h5", "", this.text, DefaultStyling.inputText) + html)
      : html;
  }
  public updateWithData(data: string) {
    super.setValue(data);
    return super.lengthError(data) || super.numberError(data);
  }
}

export class CCDateItem extends AbstractCCInputItem {
  constructor(obj: CCStructureItem<"date">) {
    super("date", obj);
  }
  public generateHTML(): string {
    let content: string = "";
    const styling = parseStyle(this);
    if (this.name) {
      content = CCText("h5", "", this.name + ": ", styling);
    }
    const attrs = convertAttrsToString(this.htmlAttributes);
    const classList = getInputItemClassList(this);
    const classAttr = convertClassArrayToAttr(classList);
    const generic = getGenericInputItemAttrs("Date", styling, this.key);
    const html = CCWrapper(
      content + `<input ${classAttr} ${attrs} ${generic} />`
    );
    return this.text
      ? CCWrapper(CCText("h5", "", this.text, DefaultStyling.inputText) + html)
      : html;
  }
  public updateWithData(data: string) {
    super.setValue(data);
    return super.lengthError(data);
  }
}

export class CCCheckItem extends AbstractCCInputItem {
  constructor(obj: CCStructureItem<"check">) {
    super("check", obj);
  }
  public generateHTML(): string {
    let content: string = "";
    const styling = parseStyle(this);
    if (this.name) {
      content = CCText("h5", "", this.name + ": ", styling);
    }
    const attrs = convertAttrsToString({
      ...this.htmlAttributes,
      ...(this.optional ? {} : { "data-check-required": "true" })
    });
    const classList = getInputItemClassList(this);
    const classAttr = convertClassArrayToAttr(classList);
    const generic = getGenericInputItemAttrs("Check", styling, this.key);
    const html = CCWrapper(
      `<input ${classAttr} ${attrs} ${generic} />` + content
    );
    return this.text
      ? CCWrapper(CCText("h5", "", this.text, DefaultStyling.inputText) + html)
      : html;
  }
  public updateWithData(data: string): boolean {
    const checkBool: boolean = data === "true" || data === "checked";
    this.element().prop("checked", checkBool);
    return !this.optional && !checkBool;
  }
}

export class CCParagraphItem extends AbstractCCInputItem {
  public wordLimit?: number;
  public richText?: boolean;

  constructor(obj: CCStructureItem<"paragraph">) {
    super("paragraph", obj);
    this.wordLimit = obj.wordLimit;
    this.richText = obj.richText;
  }
  public generateHTML(): string {
    let content: string = "";
    const styling = parseStyle(this);
    if (this.name) {
      content = CCText("h5", "confParagraphTitle", this.name + ": ", styling, {
        "data-label-for": this.key
      });
    }
    if (this.wordLimit != undefined) {
      this.htmlAttributes["data-paragraph-word-limit"] = String(this.wordLimit);
    }
    const attrs = convertAttrsToString(this.htmlAttributes);
    const classList = getInputItemClassList(this);
    const classAttr = convertClassArrayToAttr(classList);
    const generic = getGenericInputItemAttrs("Paragraph", styling, this.key);
    let html = content;
    if (this.richText) {
      html += `<div ${classAttr} ${attrs} ${generic} id="rtt-${this.key}"></div>`;
    } else {
      html += `<textarea ${classAttr} ${attrs} ${generic}></textarea>`;
    }

    return this.text
      ? CCWrapper(
          CCText("h5", "", this.text, DefaultStyling.inputText) +
            CCWrapper(html)
        )
      : CCWrapper(html);
  }
  public updateWithData(data: string): boolean {
    let isError = false;
    isError = super.lengthError(data);
    if (this.wordLimit != null) {
      const words = data.split(/(?: |\n)+/).filter((text) => {
        return text.trim().length > 0;
      });
      const paragraphTitle = $(
        `.confParagraphTitle[data-label-for="${this.key}"]`
      );
      paragraphTitle.text(
        `${this.name}: ${words.length}/${this.wordLimit} words`
      );
      if (words.length > this.wordLimit) {
        isError = true;
        paragraphTitle.css("color", "#ad310e");
      } else {
        paragraphTitle.css("color", "");
      }
    }
    if (Boolean(this.richText)) {
      $(`#rtt-${this.key}`).html(data);
    } else {
      const element = this.element();
      element.removeClass("richTextEnabled");
      element.val(data);
    }
    return isError;
  }
}

export class CCSelectItem extends AbstractCCInputItem {
  public options: string[];
  public defaultOption?: string;

  constructor(obj: CCStructureItem<"select">) {
    super("select", obj);
    this.options = obj.options;
    this.defaultOption = obj.defaultOption ?? "Choose";
  }
  private generateOptionsHTML() {
    const defaultOption = `<option class="defaultCCSelectOption" value="${this.defaultOption}">${this.defaultOption}</option>`;
    return (
      defaultOption +
      this.options
        .map((opt) => `<option value="${opt}">${opt}</option>`)
        .join("")
    );
  }
  public generateHTML(): string {
    let content: string = "";
    const styling = parseStyle(this);
    if (this.name) {
      content = CCText("h5", "", this.name + ": ", styling);
    }
    const attrs = convertAttrsToString(this.htmlAttributes);
    const classList = getInputItemClassList(this);
    const classAttr = convertClassArrayToAttr(classList);
    const generic = getGenericInputItemAttrs("Select", styling, this.key);
    const html = CCWrapper(
      content +
        (`<select ${classAttr} ${attrs} ${generic}>` +
          this.generateOptionsHTML() +
          `</select>`)
    );
    return this.text
      ? CCWrapper(CCText("h5", "", this.text, DefaultStyling.inputText) + html)
      : html;
  }
  public updateWithData(data: string): boolean {
    let isError = super.lengthError(data);
    if (!this.optional) {
      if (data === this.defaultOption) {
        isError = true;
      }
    }
    const element = this.element();
    element.val(isError ? this.defaultOption : data);
    element.find("option").removeProp("selected");
    element
      .find(`option[value=${isError ? this.defaultOption : data}]`)
      .prop("selected", true);
    return isError;
  }
}

export class CCRadioItem extends AbstractCCInputItem {
  public options: string[];
  private indexInStructure: number;

  constructor(obj: CCStructureItem<"radio">, indexInStructure: number) {
    super("radio", obj);
    this.options = obj.options;
    this.indexInStructure = indexInStructure;
  }
  private generateOptionsHTML() {
    return this.options
      .map((option, i) => {
        return (
          `<input class="confRadio" ` +
          `data-radio-value="${option}" ` +
          `type="radio" ` +
          `id="cc-radio-opt-${this.indexInStructure}-${i}" ` +
          `name="cc-radio-opt-${this.indexInStructure}" ` +
          `style="${DefaultStyling.RadioInput.compile()}"/>` +
          `<label style="${DefaultStyling.RadioLabel.compile()}" ` +
          `for="cc-radio-opt-${this.indexInStructure}-${i}">${option}</label><br/>`
        );
      })
      .join("");
  }
  public generateHTML(): string {
    let content: string = "";
    const styling = parseStyle(this);
    if (this.name) {
      content = CCText("h5", "", this.name + ": ", styling);
    }
    const attrs = convertAttrsToString(this.htmlAttributes);
    const classList = [
      "cc-radio-wrapper",
      ...getInputItemClassList(this).filter(
        (i) => i !== "form-control" && i !== "confRadio"
      )
    ];
    const classAttr = convertClassArrayToAttr(classList);
    const generic = getGenericInputItemAttrs(
      "Radio",
      styling,
      this.key,
      DefaultStyling.RadioDiv
    );
    const html = CCWrapper(
      content +
        `<div ${classAttr} ${attrs} ${generic}>` +
        this.generateOptionsHTML() +
        "</div>"
    );
    return this.text
      ? CCWrapper(CCText("h5", "", this.text, DefaultStyling.inputText) + html)
      : html;
  }
  public element(): JQuery {
    return $(`[data-key="${this.key}"]:has(.confRadio)`);
  }
  public updateWithData(data: string): boolean {
    const element = this.element();
    const buttons = element.children("input");
    buttons.removeProp("checked");
    const setOption = buttons.filter(`[data-radio-value="${data}"]`);
    setOption.prop("checked", true);
    return super.lengthError(data) || !this.options.includes(data);
  }
}

export class CCHeaderItem extends AbstractCCStructureItem {
  public name: string;
  public text?: string;

  constructor(obj: CCStructureItem<"header">) {
    super("header", obj);
    this.name = obj.name;
    this.text = obj.text;
  }
  public generateHTML(): string {
    const styling = parseStyle(this);
    const finalStyle = CCStyling.join(DefaultStyling.header, styling);
    finalStyle.override = true;
    const header = CCText("h5", "", this.name, finalStyle, this.htmlAttributes);
    if (this.text) {
      return (
        header +
        CCText(
          "h5",
          "",
          this.text,
          CCStyling.join(styling, DefaultStyling.inputText)
        )
      );
    }
    return header;
  }
}

export class CCInstructionsItem extends AbstractCCStructureItem {
  public text: string;
  public name?: string;

  constructor(obj: CCStructureItem<"instructions">) {
    super("instructions", obj);
    this.text = obj.text;
    this.name = obj.name;
  }
  public generateHTML(): string {
    const styling = parseStyle(this);
    let html = "";
    if (this.name) {
      html += CCHeader("h5", "", this.name, styling, this.htmlAttributes);
    }
    html += CCInstructions("", this.text, styling, this.htmlAttributes);
    return CCWrapper(html);
  }
}

export class CCHyperlinkItem extends AbstractCCStructureItem {
  public text?: string;
  public linkSubstring?: {
    start: number;
    end?: number;
  };
  public linkTo: string;

  constructor(obj: CCStructureItem<"hyperlink">) {
    super("hyperlink", obj);
    this.text = obj.text;
    this.linkSubstring = obj.linkSubstring;
    this.linkTo = obj.linkTo;
  }
  public generateHTML(): string {
    const styling = parseStyle(this);
    if (this.linkSubstring != undefined) {
      const pre = this.text.substring(0, this.linkSubstring.start);
      const link = this.text.substring(
        this.linkSubstring.start,
        this.linkSubstring.end
      );
      const post =
        this.linkSubstring.end == undefined
          ? ""
          : this.text.substring(this.linkSubstring.end);
      return CCWrapper(
        "<span>" +
          pre +
          CCHyperlink("", link, styling, {
            href: this.linkTo,
            ...this.htmlAttributes
          }) +
          post +
          "</span>"
      );
    }
    return CCWrapper(
      CCHyperlink("", this.text, styling, {
        href: this.linkTo,
        ...this.htmlAttributes
      })
    );
  }
}

export class CCGridItem extends AbstractCCStructureItem {
  public name?: string;
  public text?: string;
  public sections: CCStructureItem[];
  public rows: number;
  public columns: number;
  private structureObject: CCStructure;

  constructor(
    obj: CCStructureItem<"grid">,
    _: number,
    structureObject: CCStructure
  ) {
    super("grid", obj);
    this.name = obj.name;
    this.text = obj.text;
    this.sections = obj.sections;
    this.rows = obj.rows;
    this.columns = obj.columns;
    this.structureObject = structureObject;
  }
  public generateHTML(): string {
    let gridTemplateColumns = `calc(min(510px, 100%) - 2.5rem) calc(min(510px, 100%) - 2.5rem)`;
    const gridTemplateRows = "1fr ".repeat(this.rows ?? 1);
    const content = [];
    const item = this;
    this.structureObject.generateHTML(this.sections).each(function (
      index: number
    ) {
      let child = item.sections[index] ?? { orientation: "horizontal" };
      let childStyle = CCStyling.fromObject({
        "grid-template-columns": "1fr 1fr"
      });
      if (child.orientation && child.orientation === "vertical") {
        childStyle = CCStyling.fromObject({
          "grid-template-rows": "min-content auto",
          gap: "0px 0px"
        });
      }
      childStyle = CCStyling.join(childStyle, (child as any).additionalCss);
      content.push(CCGrid("", this.innerHTML, childStyle));
    });
    let input = CCGrid(
      "",
      content.join(""),
      CCStyling.join(
        CCStyling.fromObject({
          "grid-template-columns": gridTemplateColumns,
          "grid-template-rows": gridTemplateRows
        }),
        item.additionalCss
      ),
      item.htmlAttributes
    );
    if (item.text) {
      return CCWrapper(
        CCText("h5", "", item.text, DefaultStyling.inputText) + input
      );
    } else {
      return input;
    }
  }
}
export class CCCustomItem extends AbstractCCStructureItem {
  public key: string;

  constructor(obj: CCStructureItem<"custom">) {
    super("custom", obj);
    this.key = obj.key;
  }
  public generateHTML(): string {
    const cssTag =
      this.additionalCss && Object.keys(this.additionalCss.css).length > 0
        ? `style="${this.additionalCss.compile()}"`
        : "";
    return `<div id="CC-REPLACE-TARGET-${this.key}" data-key="${this.key}" ${cssTag}></div>`;
  }
}

const _abstract_map: Record<
  CCStructureItemType,
  new (
    obj: CCStructureItem,
    indexInStructure: number,
    struct: CCStructure
  ) => AbstractCCStructureItem
> = {
  string: CCStringItem,
  date: CCDateItem,
  grid: CCGridItem,
  check: CCCheckItem,
  float: CCFloatItem,
  integer: CCIntegerItem,
  radio: CCRadioItem,
  custom: CCCustomItem,
  header: CCHeaderItem,
  select: CCSelectItem,
  hyperlink: CCHyperlinkItem,
  paragraph: CCParagraphItem,
  instructions: CCInstructionsItem
};
